import { TWidget } from 'widgets/Widget';

/**
 * @description YotpoRecommendationSummary widget
 * @param Widget Base widget for extending
 * @returns Yotpo Recommendation Summary class
 */
function YotpoRecommendationSummaryClassCreator(Widget: TWidget) {
    /**
     * @category widgets
     * @subcategory product
     * @class YotpoRecommendationSummary
     * @augments Widget
     * @classdesc Shows the recommendation percentage next to the score in the Yotpo reviews widget.
     *
     * The percentage is counted by the ImportRatingsFromYotpo job and rendered into this element's preferences server side.
     *
     * @property {string} data-widget - Widget name `yotpoRecommendationSummary`
     * @property {string} data-recommendation-value - Percentage to show, already formatted
     * @property {string} data-recommendation-label - Localised caption below the percentage
     */
    class YotpoRecommendationSummary extends Widget {
        private summaryObserver?: MutationObserver;

        private giveUpTimeout?: number;

        private container?: Element;

        private injected?: HTMLElement;

        prefs() {
            return {
                recommendationValue: '',
                recommendationLabel: '',
                summarySelector: '.yotpo-bottom-line .yotpo-summary-section',
                injectedClass: 'b-yotpo_recommendation',
                widgetTimeout: 30000,
                ...super.prefs()
            };
        }

        /**
         * @description Widget initialization
         */
        init() {
            if (!this.prefs().recommendationValue) {
                return;
            }

            this.container = this.ref('self').get()?.parentElement || undefined;

            if (!this.container) {
                return;
            }

            this.inject();

            this.summaryObserver = new MutationObserver(() => this.inject());
            this.summaryObserver.observe(this.container, {
                childList: true,
                subtree: true
            });

            // The reviews widget loads lazily, but if it never arrives there is no point leaving an observer running for the life of the page.
            this.giveUpTimeout = window.setTimeout(() => {
                if (!this.injected) {
                    this.summaryObserver?.disconnect();
                }
            }, this.prefs().widgetTimeout);
        }

        /**
         * @description Add the recommendation to the Yotpo summary
         */
        inject() {
            const summary = this.container?.querySelector(this.prefs().summarySelector);

            if (!summary) {
                return;
            }

            const injectedClass = this.prefs().injectedClass;

            const alreadyShown = Array.from(summary.children).some(child => child.classList.contains(injectedClass));

            if (alreadyShown) {
                return;
            }

            const recommendation = document.createElement('div');
            const value = document.createElement('span');
            const label = document.createElement('span');

            recommendation.className = injectedClass;
            value.className = injectedClass + '-value';
            value.textContent = this.prefs().recommendationValue;
            label.className = injectedClass + '-label';
            label.textContent = this.prefs().recommendationLabel;

            recommendation.appendChild(value);
            recommendation.appendChild(label);
            summary.appendChild(recommendation);

            this.injected = recommendation;
        }

        /**
         * @description Cleanup on widget destroy.
         */
        destroy() {
            this.summaryObserver?.disconnect();

            if (this.giveUpTimeout) {
                window.clearTimeout(this.giveUpTimeout);
            }

            super.destroy();
        }
    }

    return YotpoRecommendationSummary;
}

export type TYotpoRecommendationSummary = ReturnType<typeof YotpoRecommendationSummaryClassCreator>;

export type TYotpoRecommendationSummaryInstance = InstanceType<TYotpoRecommendationSummary>;

export default YotpoRecommendationSummaryClassCreator;
